import React, { useMemo, useRef, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AudioPlayer } from './AudioPlayer';
// import { MessageRating } from '@/components/chat/MessageRating';
import {
  CHATBOT_ERROR_MESSAGE_AUDIO,
  CHATBOT_ERROR_ACCESSING_MEDIA_DEVICES,
  CHATBOT_ERROR_TRANSCRIBING_AUDIO,
  CHATBOT_WHATSAPP_MESSAGE
} from '@/utils/constants.ts';

interface BotMessageBubbleChatbotProps {
  id: string;
  content: string;
  audio?: string | null;
  timestamp?: Date;
  isStreaming?: boolean;
}

const BotMessageBubbleChatbot = React.memo(function BotMessageBubbleChatbot({
  id,
  content,
  audio,
  timestamp,
  isStreaming = false,
}: BotMessageBubbleChatbotProps) {
  // Track if markdown has been rendered to force re-render on hydration issues
  const [renderKey, setRenderKey] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const previousContentRef = useRef(content);

  // Force re-render markdown when content changes significantly (not during streaming)
  // This fixes issues where lists don't render correctly after streaming completes
  useEffect(() => {
    // Only check when NOT streaming and content has changed
    if (!isStreaming && previousContentRef.current !== content) {
      // Check if the rendered HTML is missing list elements it should have
      const hasListMarkdown = content.includes('- ') || content.includes('* ') || /^\d+\.\s/m.test(content);
      const renderedHtml = contentRef.current?.innerHTML || '';
      const hasRenderedLists = renderedHtml.includes('<ul') || renderedHtml.includes('<ol') || renderedHtml.includes('<li');
      
      // If content has list markdown but DOM doesn't have list elements, force re-render
      if (hasListMarkdown && !hasRenderedLists) {
        setRenderKey(prev => prev + 1);
      }
    }
    previousContentRef.current = content;
  }, [content, isStreaming]);

  // Determine if we should show rating
  // Don't show rating for error messages or WhatsApp offline message
  const showRating = useMemo(() => {
    if (audio) return false;
    
    const errorMessages = [
      CHATBOT_ERROR_MESSAGE_AUDIO,
      CHATBOT_ERROR_ACCESSING_MEDIA_DEVICES,
      CHATBOT_ERROR_TRANSCRIBING_AUDIO,
      CHATBOT_WHATSAPP_MESSAGE
    ];
    
    return !errorMessages.some(msg => content.includes(msg));
  }, [content, audio]);

  // Memoize components to prevent Markdown from re-creating DOM elements on every render
  const markdownComponents = useMemo(() => ({
    a: ({ ...props }: any) => (
      <a 
        {...props} 
        target="_blank" 
        rel="noopener noreferrer" 
        className="text-secondary-foreground hover:underline cursor-pointer"
        style={{ pointerEvents: 'auto' }}
        onMouseDown={(e: React.MouseEvent) => {
          // Capture click on mousedown to prevent re-render from canceling it
          e.stopPropagation();
        }}
      />
    ),
    p: ({ ...props }: any) => (
      <p {...props} className="my-2 leading-relaxed" />
    ),
    h1: ({ ...props }: any) => (
      <h1 {...props} className="text-xl font-bold mt-4 mb-2 text-foreground" />
    ),
    h2: ({ ...props }: any) => (
      <h2 {...props} className="text-lg font-bold mt-3 mb-2 text-foreground" />
    ),
    h3: ({ ...props }: any) => (
      <h3 {...props} className="text-base font-bold mt-3 mb-1.5 text-foreground" />
    ),
    h4: ({ ...props }: any) => (
      <h4 {...props} className="text-base font-bold mt-2 mb-1.5 text-foreground" />
    ),
    ul: ({ ...props }: any) => (
      <ul 
        {...props} 
        className="my-2 ml-4 space-y-1" 
        style={{ 
          listStyleType: 'disc', 
          listStylePosition: 'outside',
          paddingLeft: '1.5rem',
          display: 'block'
        }} 
      />
    ),
    ol: ({ ...props }: any) => (
      <ol 
        {...props} 
        className="my-2 ml-4 space-y-1" 
        style={{ 
          listStyleType: 'decimal', 
          listStylePosition: 'outside',
          paddingLeft: '1.5rem',
          display: 'block'
        }} 
      />
    ),
    li: ({ ...props }: any) => (
      <li 
        {...props} 
        className="ml-1 pl-1" 
        style={{ 
          display: 'list-item',
          marginLeft: '0.25rem'
        }} 
      />
    ),
    hr: ({ ...props }: any) => (
      <hr {...props} className="my-4 border-t border-border" />
    ),
    strong: ({ ...props }: any) => (
      <strong {...props} className="font-semibold text-foreground" />
    ),
    code: ({ ...props }: any) => (
      <code {...props} className="px-1.5 py-0.5 bg-muted rounded text-sm font-mono" />
    ),
    pre: ({ ...props }: any) => (
      <pre {...props} className="my-2 p-3 bg-muted rounded-lg overflow-x-auto" />
    ),
  }), []); // Empty dependency array - components never change

  return (
    <div className="flex w-full justify-start animate-in fade-in-50 slide-in-from-bottom-2 duration-300">
      <div className="flex flex-col items-start max-w-[92%]">
        <div className={cn(
          'rounded-2xl px-4 py-3 shadow-sm transition-all duration-200',
          'bg-card border border-input text-card-foreground rounded-tl-sm'
        )}>
          {audio ? (
            <div className="py-1">
              <AudioPlayer audioData={audio} from="server" />
              {timestamp && (
                <div className="text-[10px] mt-2 flex items-center text-muted-foreground opacity-70">
                  {timestamp.toLocaleTimeString('es-AR', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>
              )}
            </div>
          ) : (
            <>
              <div ref={contentRef} className="markdown-content prose prose-sm max-w-none">
                <Markdown 
                  key={renderKey}
                  remarkPlugins={[remarkGfm]}
                  components={markdownComponents}
                >
                  {content}
                </Markdown>
              </div>
              
              {timestamp && (
                <div className="text-[10px] mt-2 flex items-center text-muted-foreground opacity-70">
                  {timestamp.toLocaleTimeString('es-AR', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>
              )}

              {/* {showRating && (
                <MessageRating messageId={id} />
              )} */}
            </>
          )}
        </div>
      </div>
    </div>
  );
});

export default BotMessageBubbleChatbot;
